import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  notification,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, ApartmentOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import DashboardSection from '../dashboard/components/DashboardSection';
import { ADMIN_REVIEW_MODAL_CLASS } from '../dashboard/utils';
import './plans-dashboard.css';

const MODAL_PROPS = {
  rootClassName: ADMIN_REVIEW_MODAL_CLASS,
  className: `${ADMIN_REVIEW_MODAL_CLASS}__dialog`,
};

const scopeOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'All', value: 'all' },
];

const TiersManagement = () => {
  const [tiers, setTiers] = useState([]);
  const [scope, setScope] = useState('active');
  const [loading, setLoading] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  const [editError, setEditError] = useState('');
  const [conflictMessage, setConflictMessage] = useState('');
  const [createForm, setCreateForm] = useState({ tier_name: '', tier_rank: undefined, tier_status: 1 });
  const [editTier, setEditTier] = useState(null);
  const [search, setSearch] = useState('');

  const refresh = useCallback(async (requestedScope = scope) => {
    setLoading(true);
    try {
      const resp = await lib.getAdminTiers({ scope: requestedScope });
      if (resp?.status === 'error') {
        const msg = resp?.msg || 'Unable to load tiers';
        setConflictMessage(msg);
        notification.error({ message: msg, placement: 'topLeft' });
        setTiers([]);
        return;
      }
      const body = resp?.data;
      if (body?.error || resp?.error) {
        const msg = body?.message || 'Unable to load tiers';
        setConflictMessage(msg);
        notification.error({ message: msg, placement: 'topLeft' });
        setTiers([]);
        return;
      }
      setConflictMessage('');
      const list = Array.isArray(body?.data?.tiers) ? body.data.tiers : [];
      const sorted = [...list].sort((a, b) => Number(a?.tier_rank || 0) - Number(b?.tier_rank || 0));
      setTiers(sorted);
    } catch (error) {
      const msg = error?.message || 'Unable to load tiers';
      setConflictMessage(msg);
      notification.error({ message: msg, placement: 'topLeft' });
      setTiers([]);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  React.useEffect(() => {
    refresh(scope);
  }, [scope, refresh]);

  const suggestedRank = useMemo(() => {
    const ranks = tiers.map((t) => Number(t?.tier_rank || 0)).filter((v) => Number.isFinite(v));
    const highest = ranks.length ? Math.max(...ranks) : 0;
    return highest + 10;
  }, [tiers]);

  const filteredTiers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return tiers;
    return tiers.filter((tier) => (tier?.tier_name || '').toLowerCase().includes(query));
  }, [tiers, search]);

  const openCreateModal = () => {
    setCreateError('');
    setCreateForm({ tier_name: '', tier_rank: suggestedRank, tier_status: 1 });
    setOpenCreate(true);
  };

  const submitCreate = async () => {
    if (!createForm?.tier_name?.trim() || createForm?.tier_rank == null) {
      setCreateError('Tier name and rank are required.');
      return;
    }

    setSaving(true);
    setCreateError('');
    try {
      const payload = {
        tier_name: createForm.tier_name.trim(),
        tier_rank: Number(createForm.tier_rank),
        tier_status: Number(createForm?.tier_status ?? 1),
      };
      const resp = await lib.createAdminTier(payload);
      if (resp?.status === 'error') {
        const msg = resp?.msg || 'Unable to create tier';
        setCreateError(msg);
        setConflictMessage(msg);
        return;
      }
      const body = resp?.data;
      if (body?.error || resp?.error) {
        const msg = body?.message || 'Unable to create tier';
        setCreateError(msg);
        setConflictMessage(msg);
        return;
      }
      setConflictMessage('');
      notification.success({ message: body?.message || 'Tier created successfully', placement: 'topLeft' });
      setOpenCreate(false);
      refresh(scope);
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Unable to create tier';
      setCreateError(msg);
      setConflictMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    if (!editTier?.tier_id) return;

    const payload = {};
    if (editTier?.tier_name != null) payload.tier_name = String(editTier.tier_name).trim();
    if (editTier?.tier_rank != null) payload.tier_rank = Number(editTier.tier_rank);
    if (editTier?.tier_status != null) payload.tier_status = Number(editTier.tier_status);

    if (!Object.keys(payload).length) {
      setEditError('No fields to update.');
      return;
    }

    setSaving(true);
    setEditError('');
    try {
      const resp = await lib.updateAdminTier(editTier.tier_id, payload);
      if (resp?.status === 'error') {
        const msg = resp?.msg || 'Unable to update tier';
        setEditError(msg);
        setConflictMessage(msg);
        return;
      }
      const body = resp?.data;
      if (body?.error || resp?.error) {
        const msg = body?.message || 'Unable to update tier';
        setEditError(msg);
        setConflictMessage(msg);
        return;
      }
      setConflictMessage('');
      notification.success({ message: body?.message || 'Tier updated successfully', placement: 'topLeft' });
      setOpenEdit(false);
      setEditTier(null);
      refresh(scope);
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Unable to update tier';
      setEditError(msg);
      setConflictMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  const deactivateTier = async (tier) => {
    try {
      setSaving(true);
      const resp = await lib.deactivateAdminTier(tier?.tier_id);
      if (resp?.status === 'error') {
        const msg = resp?.msg || 'Unable to deactivate tier';
        setConflictMessage(msg);
        notification.error({ message: msg, placement: 'topLeft' });
        return;
      }
      const body = resp?.data;
      if (body?.error || resp?.error) {
        const msg = body?.message || 'Unable to deactivate tier';
        setConflictMessage(msg);
        notification.error({ message: msg, placement: 'topLeft' });
        return;
      }
      setConflictMessage('');
      notification.success({ message: body?.message || 'Tier deactivated successfully', placement: 'topLeft' });
      refresh(scope);
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Unable to deactivate tier';
      setConflictMessage(msg);
      notification.error({ message: msg, placement: 'topLeft' });
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Rank',
      dataIndex: 'tier_rank',
      key: 'tier_rank',
      width: 90,
      sorter: (a, b) => Number(a?.tier_rank || 0) - Number(b?.tier_rank || 0),
    },
    {
      title: 'Name',
      dataIndex: 'tier_name',
      key: 'tier_name',
      width: 200,
    },
    {
      title: 'Plans',
      dataIndex: 'plan_count',
      key: 'plan_count',
      width: 100,
      render: (value) => Number(value || 0),
    },
    {
      title: 'Facilities',
      dataIndex: 'facility_count',
      key: 'facility_count',
      width: 100,
      render: (value) => Number(value || 0),
    },
    {
      title: 'Status',
      dataIndex: 'tier_status',
      key: 'tier_status',
      width: 140,
      render: (value) => Number(value) === 1 ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditError('');
              setEditTier({
                tier_id: record?.tier_id,
                tier_name: record?.tier_name,
                tier_rank: Number(record?.tier_rank),
                tier_status: Number(record?.tier_status),
              });
              setOpenEdit(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title={`Deactivate ${record?.tier_name}?`}
            description="This will fail if active plans/facilities still reference it."
            okText="Deactivate"
            cancelText="Cancel"
            onConfirm={() => deactivateTier(record)}
          >
            <Button danger size="small" disabled={Number(record?.tier_status) === 0}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {conflictMessage ? (
        <Alert type="error" showIcon message={conflictMessage} style={{ marginBottom: 12 }} closable onClose={() => setConflictMessage('')} />
      ) : null}

      <DashboardSection
        id="plans-tiers"
        icon={<ApartmentOutlined />}
        title="Tiers"
        subtitle={`${filteredTiers.length} tier${filteredTiers.length === 1 ? '' : 's'} · higher rank = higher privilege`}
        collapsible={false}
        variant="content"
        action={(
          <div className="plans-toolbar plans-toolbar--inline">
            <Input
              className="plans-toolbar__search"
              size="small"
              placeholder="Search tiers…"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
            <Select
              className="plans-toolbar__select"
              size="small"
              value={scope}
              options={scopeOptions}
              onChange={setScope}
            />
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refresh(scope)} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateModal}>
              New tier
            </Button>
          </div>
        )}
      >
        <div className="plans-table-wrap">
          <Table
            columns={columns}
            dataSource={filteredTiers}
            loading={loading}
            rowKey={(row) => String(row?.tier_id)}
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${total} tiers`,
            }}
          />
        </div>
      </DashboardSection>

      <Modal
        {...MODAL_PROPS}
        open={openCreate}
        title="Create Tier"
        onCancel={() => setOpenCreate(false)}
        onOk={submitCreate}
        okText="Create Tier"
        confirmLoading={saving}
      >
        {createError ? <Alert type="error" showIcon message={createError} style={{ marginBottom: 12 }} /> : null}
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Tier name</div>
            <Input
              value={createForm?.tier_name}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, tier_name: e.target.value }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Tier rank</div>
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              value={createForm?.tier_rank}
              onChange={(value) => setCreateForm((prev) => ({ ...prev, tier_rank: value }))}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>Suggested next rank: {suggestedRank}</div>
          </div>
        </Space>
      </Modal>

      <Modal
        {...MODAL_PROPS}
        open={openEdit}
        title={`Edit Tier${editTier?.tier_name ? ` - ${editTier.tier_name}` : ''}`}
        onCancel={() => setOpenEdit(false)}
        onOk={submitEdit}
        okText="Save"
        confirmLoading={saving}
      >
        {editError ? <Alert type="error" showIcon message={editError} style={{ marginBottom: 12 }} /> : null}
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Tier name</div>
            <Input
              value={editTier?.tier_name}
              onChange={(e) => setEditTier((prev) => ({ ...prev, tier_name: e.target.value }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Tier rank</div>
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              value={editTier?.tier_rank}
              onChange={(value) => setEditTier((prev) => ({ ...prev, tier_rank: value }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Status</div>
            <Select
              value={Number(editTier?.tier_status)}
              style={{ width: '100%' }}
              options={[
                { label: 'Active', value: 1 },
                { label: 'Inactive', value: 0 },
              ]}
              onChange={(value) => setEditTier((prev) => ({ ...prev, tier_status: value }))}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default TiersManagement;
